import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Send, Loader2, Shield, Smile, Image as ImageIcon, Users, ArrowLeft, LifeBuoy, Trash2, Pin, MoreVertical, X, Reply, Palette, Star } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { db, storage } from "../config/firebase";
import { collection, addDoc, query, orderBy, onSnapshot, serverTimestamp, where, limit, doc, setDoc, deleteDoc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { motion, useDragControls } from "framer-motion";
import chatPattern from "../assets/wallpapers/chat-pattern.svg";
import EWave from "../assets/wallpapers/EWave.svg";
import SND from "../assets/wallpapers/SND.svg";
import SNR from "../assets/wallpapers/SNR.svg";
import SNRBO from "../assets/wallpapers/SNRBO.svg";
import SUPPLY from "../assets/wallpapers/SUPPLY.svg";
import VNW2N01 from "../assets/wallpapers/VNW2N01.svg";
import Wave from "../assets/wallpapers/wave.svg";
import Crypto from "../assets/wallpapers/Crypto.svg";
import Candle from "../assets/wallpapers/candlestick-doodles.svg";
import Demand from "../assets/wallpapers/dEMAND.svg";
import MarketStructure from "../assets/wallpapers/2RgXJ01.svg";
import TradingLevels from "../assets/wallpapers/trading-levels.svg";

// Helper function to format date
const formatDate = (date) => {
    if (!date) return '';
    const now = new Date();
    const msgDate = date.toDate ? date.toDate() : new Date(date);

    const isToday = msgDate.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = msgDate.toDateString() === yesterday.toDateString();

    if (isToday) return 'Today';
    if (isYesterday) return 'Yesterday';

    return msgDate.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: msgDate.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
};

// Helper function to format time
const formatTime = (date) => {
    if (!date) return '';
    const msgDate = date.toDate ? date.toDate() : new Date(date);
    return msgDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    });
};

const wallpapers = [
    { id: 'default', name: 'Bull & Bear', src: chatPattern },
    { id: 'wave', name: 'Wave Analysis', src: Wave },
    { id: 'ewave', name: 'Elliott Wave', src: EWave },
    { id: 'snd', name: 'Supply & Demand', src: SND },
    { id: 'snr', name: 'Support & Res', src: SNR },
    { id: 'supply', name: 'Supply Zones', src: SUPPLY },
    { id: 'vnw', name: 'VNW Pattern', src: VNW2N01 },
    { id: 'crypto', name: 'Crypto', src: Crypto },
    { id: 'candle', name: 'Candlesticks', src: Candle },
    { id: 'market', name: 'Market Structure', src: MarketStructure },
    { id: 'demand', name: 'Demand Zones', src: Demand },
    { id: 'levels', name: 'Trading Levels', src: TradingLevels },
];

export default function AdminSupport() {
    const navigate = useNavigate();
    const { user } = useAuth() || { user: null };
    const [messageContent, setMessageContent] = useState("");
    const [messages, setMessages] = useState([]);
    const [conversations, setConversations] = useState([]);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(true);
    const [showEmoji, setShowEmoji] = useState(false);
    const [pinnedMessage, setPinnedMessage] = useState(null);
    const [showMessageMenu, setShowMessageMenu] = useState(null);
    const [replyingTo, setReplyingTo] = useState(null); // { id, author, content }
    const [editingMessage, setEditingMessage] = useState(null); // { id, content }
    const [showWallpaperMenu, setShowWallpaperMenu] = useState(false);
    const [currentWallpaper, setCurrentWallpaper] = useState(() => {
        return localStorage.getItem('admin_chat_wallpaper') || wallpapers[0].src;
    });

    const handleWallpaperChange = (src) => {
        setCurrentWallpaper(src);
        localStorage.setItem('admin_chat_wallpaper', src);
        setShowWallpaperMenu(false);
    };

    // Image Handling State
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [isDragOver, setIsDragOver] = useState(false);

    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const menuRef = useRef(null);

    const isAdmin = user?.role === "admin";

    // For admin: Get all conversations
    useEffect(() => {
        if (!isAdmin) return;

        const messagesRef = collection(db, "admin_support");
        const q = query(messagesRef, orderBy("created_at", "desc"));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const allMessages = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            const convMap = new Map();
            allMessages.forEach(msg => {
                const userEmail = msg.user_email;
                if (!convMap.has(userEmail)) {
                    convMap.set(userEmail, {
                        user_email: userEmail,
                        user_name: msg.user_name || userEmail,
                        last_message: msg.content,
                        last_time: msg.created_at,
                        messages: []
                    });
                }
                convMap.get(userEmail).messages.push(msg);
            });

            setConversations(Array.from(convMap.values()));
            setLoading(false);
        }, (error) => {
            console.error("Error fetching conversations:", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [isAdmin]);

    // For student OR selected conversation: Get messages
    useEffect(() => {
        if (!user) return;

        const userEmail = isAdmin ? selectedConversation : user.email;
        if (!userEmail) {
            setLoading(false);
            return;
        }

        const messagesRef = collection(db, "admin_support");
        const q = query(
            messagesRef,
            where("user_email", "==", userEmail),
            orderBy("created_at", "asc"),
            limit(100)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const newMessages = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setMessages(newMessages);

            // Notification Sound
            const lastMsg = newMessages[newMessages.length - 1];
            if (!loading && lastMsg && lastMsg.author_email !== user?.email) {
                const audio = new Audio("https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3");
                audio.volume = 0.5;
                audio.play().catch(e => console.log("Audio play failed:", e));
            }

            // Check for pinned message
            const pinned = newMessages.find(m => m.pinned);
            setPinnedMessage(pinned || null);

            setLoading(false); // Restore loading state update

            setTimeout(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
            }, 100);
        }, (error) => {
            console.error("Error fetching messages:", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [user, isAdmin, selectedConversation]);

    // Mark messages as read when page is opened (for students)
    useEffect(() => {
        if (!user?.email || isAdmin) return;

        const markAsRead = async () => {
            try {
                const userReadRef = doc(db, 'user_read_status', `${user.email}_admin_support`);
                await setDoc(userReadRef, {
                    last_read: serverTimestamp(),
                    user_email: user.email
                }, { merge: true });
            } catch (error) {
                console.error('Error marking as read:', error);
            }
        };

        markAsRead();
    }, [user?.email, isAdmin]);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setShowMessageMenu(null);
            }
        };

        if (showMessageMenu) {
            document.addEventListener("click", handleClickOutside);
        }
        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, [showMessageMenu]);

    // Reply Handler
    const handleReply = (message) => {
        setReplyingTo({
            id: message.id,
            author: message.author_name,
            content: message.content
        });
        document.getElementById("admin-chat-input")?.focus();
    };

    // Edit Handler
    const handleEditClick = (message) => {
        setEditingMessage({
            id: message.id,
            content: message.content
        });
        setMessageContent(message.content);
        document.getElementById("admin-chat-input")?.focus();
        setShowMessageMenu(null);
    };

    const handleCancelEdit = () => {
        setEditingMessage(null);
        setReplyingTo(null);
        setMessageContent("");
    };

    const handleFileSelect = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            if (file.size > 5 * 1024 * 1024) {
                alert("File size too large (Max 5MB)");
                return;
            }
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const removeImage = () => {
        setImageFile(null);
        setImagePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleSend = async () => {
        if ((!messageContent.trim() && !imageFile) || sending) return;

        setSending(true);
        try {
            let imageUrl = null;

            // Upload Image if exists
            if (imageFile) {
                const storageRef = ref(storage, `chat_images/${Date.now()}_${imageFile.name}`);
                await uploadBytes(storageRef, imageFile);
                imageUrl = await getDownloadURL(storageRef);
            }

            if (editingMessage) {
                // UPDATE existing message
                await updateDoc(doc(db, "admin_support", editingMessage.id), {
                    content: messageContent,
                    is_edited: true,
                    updated_at: serverTimestamp()
                });
                setEditingMessage(null);
            } else {
                // CREATE new message
                const userEmail = isAdmin ? selectedConversation : user.email;
                const userName = isAdmin ? "Admin" : (user?.firstName || user?.email?.split('@')[0] || "User");

                await addDoc(collection(db, "admin_support"), {
                    content: messageContent,
                    imageUrl: imageUrl, // Add image URL
                    author_name: userName,
                    author_email: user?.email,
                    author_role: isAdmin ? "admin" : "user",
                    user_email: userEmail,
                    user_name: isAdmin ? selectedConversation : userName,
                    created_at: serverTimestamp(),
                    pinned: false,
                    // Reply data
                    reply_to: replyingTo ? {
                        id: replyingTo.id,
                        author: replyingTo.author,
                        content: replyingTo.content
                    } : null
                });
            }

            setMessageContent("");
            setReplyingTo(null);
            removeImage(); // Clear image state
            setShowEmoji(false);
        } catch (error) {
            console.error("Error sending/updating message:", error);
            alert("Gagal hantar/update mesej. Sila cuba lagi.");
        } finally {
            setSending(false);
        }
    };

    // Delete message (Admin OR Owner)
    const handleDeleteMessage = async (messageId, authorEmail) => {
        // Allow if Admin OR if the message belongs to current user
        if (!isAdmin && authorEmail !== user?.email) {
            alert("You can only delete your own messages.");
            return;
        }

        if (!window.confirm("Delete this message?")) return;

        try {
            await deleteDoc(doc(db, "admin_support", messageId));
            setShowMessageMenu(null);
        } catch (error) {
            console.error("Error deleting message:", error);
            alert("Gagal delete mesej.");
        }
    };

    // Pin/Unpin message (Admin only)
    const handlePinMessage = async (messageId, currentlyPinned) => {
        if (!isAdmin) return;

        try {
            // Unpin all other messages first
            if (!currentlyPinned) {
                for (const msg of messages) {
                    if (msg.pinned) {
                        await updateDoc(doc(db, "admin_support", msg.id), { pinned: false });
                    }
                }
            }

            // Toggle pin on this message
            await updateDoc(doc(db, "admin_support", messageId), {
                pinned: !currentlyPinned
            });
            setShowMessageMenu(null);
        } catch (error) {
            console.error("Error pinning message:", error);
            alert("Gagal pin mesej.");
        }
    };

    // Group messages by date
    const getMessagesWithDateSeparators = () => {
        const result = [];
        let lastDate = null;

        messages.forEach((message, index) => {
            const msgDate = message.created_at ? formatDate(message.created_at) : 'Unknown';

            if (msgDate !== lastDate) {
                result.push({ type: 'date', date: msgDate, id: `date-${index}` });
                lastDate = msgDate;
            }
            result.push({ type: 'message', ...message });
        });

        return result;
    };

    // If admin and no conversation selected, show conversation list
    if (isAdmin && !selectedConversation) {
        return (
            <div className="h-screen bg-gray-100 flex flex-col overflow-hidden">
                {/* Header */}
                <div className="bg-white border-b border-gray-200 flex-shrink-0">
                    <div className="px-4 py-3 flex items-center gap-3">
                        <button
                            onClick={() => navigate('/community')}
                            className="p-2 -ml-2 text-gray-600 hover:text-emerald-600 hover:bg-emerald-50 rounded-full transition-colors"
                        >
                            <ArrowLeft size={22} />
                        </button>

                        <div className="bg-emerald-500 p-2 rounded-full">
                            <LifeBuoy size={18} className="text-white" />
                        </div>

                        <div className="flex-1 min-w-0">
                            <h1 className="text-base font-bold text-gray-900 truncate">Admin Support</h1>
                            <p className="text-xs text-gray-500">{conversations.length} conversations</p>
                        </div>
                    </div>
                </div>

                {/* Conversation List */}
                <div className="flex-1 overflow-y-auto bg-white">
                    {loading ? (
                        <div className="flex items-center justify-center h-full">
                            <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
                        </div>
                    ) : conversations.length === 0 ? (
                        <div className="text-center py-20">
                            <Shield className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                            <p className="text-gray-500">No conversations yet</p>
                        </div>
                    ) : (
                        conversations.map((conv) => (
                            <button
                                key={conv.user_email}
                                onClick={() => setSelectedConversation(conv.user_email)}
                                className="w-full text-left p-4 border-b border-gray-100 hover:bg-emerald-50 transition-colors flex items-center gap-3"
                            >
                                <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center text-white font-bold">
                                    {conv.user_name?.charAt(0)?.toUpperCase() || 'U'}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-gray-800 truncate">{conv.user_name}</p>
                                    <p className="text-sm text-gray-500 truncate">{conv.last_message}</p>
                                </div>
                            </button>
                        ))
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="h-screen bg-gray-100 flex flex-col overflow-hidden">

            {/* Compact Header */}
            <div className="bg-white border-b border-gray-200 flex-shrink-0">
                <div className="px-4 py-3 flex items-center gap-3">
                    <button
                        onClick={() => isAdmin ? setSelectedConversation(null) : navigate('/community')}
                        className="p-2 -ml-2 text-gray-600 hover:text-emerald-600 hover:bg-emerald-50 rounded-full transition-colors"
                    >
                        <ArrowLeft size={22} />
                    </button>

                    <div className="bg-emerald-500 p-2 rounded-full">
                        <LifeBuoy size={18} className="text-white" />
                    </div>

                    <div className="flex-1 min-w-0">
                        <h1 className="text-base font-bold text-gray-900 truncate">
                            {isAdmin ? selectedConversation : 'Admin Support'}
                        </h1>
                        <p className="text-xs text-gray-500">{messages.length} messages</p>
                    </div>

                    <button
                        onClick={() => setShowWallpaperMenu(true)}
                        className="p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
                        title="Change Wallpaper"
                    >
                        <Palette size={20} />
                    </button>
                </div>
            </div>

            {/* Pinned Message Banner */}
            {pinnedMessage && (
                <div className="bg-emerald-50 border-b border-emerald-200 px-4 py-2 flex items-center gap-2">
                    <Pin size={14} className="text-emerald-600 flex-shrink-0" />
                    <p className="text-sm text-emerald-800 truncate flex-1">{pinnedMessage.content}</p>
                    {isAdmin && (
                        <button
                            onClick={() => handlePinMessage(pinnedMessage.id, true)}
                            className="text-emerald-600 hover:text-emerald-800"
                        >
                            <X size={16} />
                        </button>
                    )}
                </div>
            )}

            {/* Wallpaper Menu Modal */}
            {showWallpaperMenu && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setShowWallpaperMenu(false)}>
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                            <h3 className="font-bold text-gray-800">Choose Background</h3>
                            <button onClick={() => setShowWallpaperMenu(false)} className="p-1 hover:bg-gray-100 rounded-full">
                                <X size={20} className="text-gray-500" />
                            </button>
                        </div>
                        <div className="p-4 grid grid-cols-3 gap-3 max-h-[60vh] overflow-y-auto">
                            {wallpapers.map((wp) => (
                                <button
                                    key={wp.id}
                                    onClick={() => handleWallpaperChange(wp.src)}
                                    className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${currentWallpaper === wp.src ? 'border-emerald-500 ring-2 ring-emerald-200' : 'border-gray-200 hover:border-emerald-300'}`}
                                >
                                    <img src={wp.src} alt={wp.name} className="w-full h-full object-cover" />
                                    <div className="absolute inset-x-0 bottom-0 bg-black/60 p-1">
                                        <p className="text-[10px] text-white text-center truncate">{wp.name}</p>
                                    </div>
                                    {currentWallpaper === wp.src && (
                                        <div className="absolute top-1 right-1 bg-emerald-500 text-white rounded-full p-0.5">
                                            <Star size={10} fill="currentColor" />
                                        </div>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Chat Messages - Full Height Area */}
            <div className="flex-1 relative overflow-hidden bg-[#efe7dd] dark:bg-gray-900">
                {/* Fixed Background Pattern Overlay */}
                <div
                    className="absolute inset-0 z-0 pointer-events-none opacity-[0.15] dark:opacity-[0.1]"
                    style={{
                        backgroundImage: `url(${currentWallpaper})`,
                        backgroundSize: '300px',
                        backgroundRepeat: 'repeat'
                    }}
                />

                {/* Scrollable Content Layer */}
                <div className="absolute inset-0 overflow-y-auto z-10 px-3 py-4">
                    {loading ? (
                        <div className="flex items-center justify-center h-full py-20">
                            <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
                        </div>
                    ) : messages.length === 0 ? (
                        <div className="text-center py-20">
                            <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                                <Shield className="w-10 h-10 text-emerald-500" />
                            </div>
                            <h3 className="text-lg font-semibold mb-2 text-gray-800">
                                {isAdmin ? "No messages yet" : "Start a conversation"}
                            </h3>
                            <p className="text-gray-500 text-sm">
                                {isAdmin ? "Waiting for user message" : "Send a message to get help"}
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {getMessagesWithDateSeparators().map((item) => {
                                // Date Separator
                                if (item.type === 'date') {
                                    return (
                                        <div key={item.id} className="flex justify-center my-4">
                                            <span className="bg-white/80 backdrop-blur-sm text-gray-500 text-xs font-medium px-4 py-1.5 rounded-full shadow-sm border border-gray-100">
                                                {item.date}
                                            </span>
                                        </div>
                                    );
                                }

                                // Message Bubble using Motion for Swipe
                                const message = item;
                                const isMe = message.author_email === user?.email;
                                const isAdminMsg = message.author_role === 'admin';

                                return (
                                    <motion.div
                                        key={message.id}
                                        className={`flex ${isMe ? 'justify-end' : 'justify-start'} items-start gap-1 relative touch-pan-y`}
                                        drag="x"
                                        dragConstraints={{ left: 0, right: 0 }}
                                        dragElastic={0.1}
                                        onDragEnd={(e, info) => {
                                            if (info.offset.x > 50) {
                                                handleReply(message);
                                                if (navigator.vibrate) navigator.vibrate(50);
                                            }
                                        }}
                                        whileDrag={{ scale: 1.02 }}
                                    >
                                        <div className={`max-w-[75%]`}>
                                            {/* Author name */}
                                            {!isMe && (
                                                <p className={`text-xs font-bold mb-1 ml-3 ${isAdminMsg ? 'text-emerald-600' : 'text-gray-800'}`}>
                                                    {message.author_name} {isAdminMsg && '(Admin)'}
                                                </p>
                                            )}

                                            <div className={`relative px-4 py-2.5 rounded-2xl shadow-sm ${isAdminMsg
                                                ? 'bg-emerald-500 text-white rounded-bl-md'
                                                : isMe
                                                    ? 'bg-blue-500 text-white rounded-br-md'
                                                    : 'bg-white text-gray-800 rounded-bl-md border border-gray-100'
                                                } ${message.pinned ? 'ring-2 ring-emerald-400' : ''}`}>

                                                {/* Image Render */}
                                                {message.imageUrl && (
                                                    <div className="mb-2 -mx-1 mt-1">
                                                        <img
                                                            src={message.imageUrl}
                                                            alt="Attachment"
                                                            className="rounded-lg max-h-[300px] w-full object-cover bg-gray-100 border border-white/20"
                                                            loading="lazy"
                                                            onClick={() => window.open(message.imageUrl, '_blank')}
                                                        />
                                                    </div>
                                                )}

                                                {/* Pin indicator */}
                                                {message.pinned && (
                                                    <Pin size={12} className="absolute -top-1 -right-1 text-emerald-600 bg-white rounded-full p-0.5" />
                                                )}

                                                {/* Reply Context */}
                                                {message.reply_to && (
                                                    <div className={`text-xs p-2 mb-2 rounded border-l-4 cursor-pointer hover:opacity-80 transition-opacity ${isAdminMsg || isMe
                                                        ? 'bg-white/20 border-white/50 text-white'
                                                        : 'bg-gray-200 border-gray-400 text-black'
                                                        }`}
                                                        onClick={() => {
                                                            const el = document.getElementById(`msg-${message.reply_to.id}`);
                                                            el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                            el?.classList.add('ring-2', 'ring-offset-2', 'ring-blue-400');
                                                            setTimeout(() => el?.classList.remove('ring-2', 'ring-offset-2', 'ring-blue-400'), 2000);
                                                        }}>
                                                        <p className="font-bold mb-0.5 opacity-90">{message.reply_to.author}</p>
                                                        <p className="truncate opacity-75">{message.reply_to.content || "Image attachment"}</p>
                                                    </div>
                                                )}

                                                <p className="text-[15px] leading-relaxed whitespace-pre-wrap break-words">
                                                    {message.content}
                                                    {message.is_edited && <span className={`text-[10px] italic ml-1 ${isAdminMsg || isMe ? 'text-white/70' : 'text-gray-400'}`}>(edited)</span>}
                                                </p>

                                                {/* Time stamp */}
                                                <p className={`text-[10px] mt-1 text-right ${isAdminMsg || isMe ? 'text-white/70' : 'text-gray-400'
                                                    }`}>
                                                    {formatTime(message.created_at)}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Unified Menu (Right side for own messages, Left for others if admin/reply needed) */}
                                        <div className={`relative flex-shrink-0 mt-2 ${isMe ? 'order-first mr-1' : 'ml-1'}`}>
                                            <button
                                                onClick={() => setShowMessageMenu(showMessageMenu === message.id ? null : message.id)}
                                                className="bg-white shadow-sm rounded-full p-1 border border-gray-200 hover:bg-gray-50 opacity-60 group-hover:opacity-100 transition-opacity"
                                            >
                                                <MoreVertical size={14} className="text-gray-400" />
                                            </button>

                                            {showMessageMenu === message.id && (
                                                <div ref={menuRef} className={`absolute top-8 ${isMe ? 'right-0 md:left-0' : 'left-0'} bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-[9999] min-w-[140px]`}>
                                                    {/* Reply Option */}
                                                    <button
                                                        onClick={() => {
                                                            handleReply(message);
                                                            setShowMessageMenu(null);
                                                        }}
                                                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center gap-2 text-gray-700"
                                                    >
                                                        <Reply size={14} />
                                                        Reply
                                                    </button>

                                                    {/* Edit Option (Owner Only) */}
                                                    {isMe && (
                                                        <button
                                                            onClick={() => handleEditClick(message)}
                                                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center gap-2 text-gray-700"
                                                        >
                                                            <div className="w-3.5 h-3.5"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg></div>
                                                            {/* SVG Edit Icon Verified */}
                                                            Edit
                                                        </button>
                                                    )}

                                                    {/* Delete Option (Admin OR Owner) */}
                                                    {(isAdmin || isMe) && (
                                                        <button
                                                            onClick={() => handleDeleteMessage(message.id, message.author_email)}
                                                            className="w-full text-left px-3 py-2 text-sm hover:bg-red-50 text-red-600 flex items-center gap-2"
                                                        >
                                                            <Trash2 size={14} />
                                                            Delete
                                                        </button>
                                                    )}

                                                    {/* Pin Option (Admin Only) */}
                                                    {isAdmin && (
                                                        <button
                                                            onClick={() => handlePinMessage(message.id, message.pinned)}
                                                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center gap-2 text-gray-700 border-t border-gray-100 mt-1 pt-1"
                                                        >
                                                            <Pin size={14} />
                                                            {message.pinned ? 'Unpin' : 'Pin'}
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </motion.div>
                                );
                            })}
                            <div ref={messagesEndRef} className="h-2" />
                        </div>
                    )}
                </div>
            </div>

            {/* Input Area - Fixed at Bottom */}
            <div className="flex-shrink-0 bg-white border-t border-gray-200 px-4 py-3 relative">

                {/* Image Preview Overlay */}
                {imagePreview && (
                    <div className="absolute bottom-full left-0 right-0 p-3 bg-white/95 backdrop-blur-sm border-t border-gray-200 shadow-sm z-30 mb-[1px] rounded-t-xl">
                        <div className="relative inline-block">
                            <img src={imagePreview} alt="Preview" className="h-20 w-auto rounded-lg border border-gray-300 shadow-sm object-cover" />
                            <button
                                onClick={removeImage}
                                className="absolute -top-2 -right-2 bg-gray-800 text-white rounded-full p-1 shadow-md hover:bg-black transition-colors"
                            >
                                <X size={12} />
                            </button>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">Image selected</p>
                    </div>
                )}

                {/* Edit/Reply Preview */}
                {(replyingTo || editingMessage) && (
                    <div className={`absolute bottom-full left-0 right-0 bg-gray-50 border-t border-gray-200 p-3 flex justify-between items-center z-20 ${imagePreview ? 'mb-[110px]' : ''}`}>
                        <div className="flex items-center gap-3 overflow-hidden">
                            <div className={`w-1 h-8 ${editingMessage ? 'bg-orange-500' : 'bg-emerald-500'} rounded-full flex-shrink-0`}></div>
                            <div>
                                <p className={`text-xs font-bold ${editingMessage ? 'text-orange-600' : 'text-emerald-600'}`}>
                                    {editingMessage ? 'Editing Message' : `Replying to ${replyingTo.author}`}
                                </p>
                                <p className="text-sm text-black font-medium truncate max-w-[200px] md:max-w-md">
                                    {editingMessage ? '' : replyingTo.content || "Image attachment"}
                                </p>
                            </div>
                        </div>
                        <button onClick={handleCancelEdit} className="p-2 hover:bg-gray-200 rounded-full">
                            <X size={16} className="text-gray-500" />
                        </button>
                    </div>
                )}

                {/* Emoji Picker */}
                {showEmoji && (
                    <div className="absolute bottom-20 left-4 right-4 bg-white rounded-2xl shadow-xl p-3 border border-gray-200 z-10">
                        <div className="grid grid-cols-8 gap-2">
                            {['👍', '👎', '❤️', '🔥', '🚀', '💯', '🎯', '📈', '📉', '💡', '⚠️', '✅', '❌', '🤔', '👀', '🙏', '😀', '😂', '😍', '🥳', '😎', '🤑', '💪', '👏'].map((emoji) => (
                                <button
                                    key={emoji}
                                    onClick={() => {
                                        setMessageContent(prev => prev + emoji);
                                        setShowEmoji(false);
                                    }}
                                    className="text-2xl hover:bg-gray-100 rounded-lg p-2 transition-colors active:scale-95"
                                >
                                    {emoji}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <div className="flex items-center gap-2 w-full">
                    <button
                        onClick={() => fileInputRef.current?.click()}
                        className={`flex-shrink-0 p-2 rounded-full transition-all ${imageFile ? 'text-emerald-500 bg-emerald-50' : 'text-gray-400 hover:text-emerald-500 hover:bg-emerald-50'}`}
                    >
                        <ImageIcon size={20} />
                    </button>
                    <input
                        type="file"
                        ref={fileInputRef}
                        accept="image/*"
                        className="hidden"
                        onChange={handleFileSelect}
                    />

                    <button
                        onClick={() => setShowEmoji(!showEmoji)}
                        className={`flex-shrink-0 p-2 rounded-full transition-all ${showEmoji ? 'text-emerald-500 bg-emerald-50' : 'text-gray-400 hover:text-emerald-500 hover:bg-emerald-50'}`}
                    >
                        <Smile size={20} />
                    </button>

                    <input
                        id="admin-chat-input"
                        type="text"
                        value={messageContent}
                        onChange={(e) => setMessageContent(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                handleSend();
                            }
                        }}
                        placeholder={replyingTo ? `Replying to ${replyingTo.author}...` : editingMessage ? "Edit message..." : "Message..."}
                        className="flex-1 min-w-0 bg-gray-100 border-0 text-gray-900 placeholder-gray-500 rounded-full px-4 py-2.5 text-[15px] focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all"
                        disabled={sending}
                    />

                    <button
                        onClick={handleSend}
                        disabled={(!messageContent.trim() && !imageFile) || sending}
                        className="flex-shrink-0 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 disabled:hover:bg-emerald-500 text-white p-2.5 rounded-full shadow-md transition-all active:scale-95"
                    >
                        {sending ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} />}
                    </button>
                </div>
            </div>

            {/* Click outside to close menu - Handled by effect */}
        </div>
    );
}
